import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Grid from "@mui/material/Grid";
import Drawer from "@mui/material/Drawer";
import FloatLabel from "../../UiComponents/FloatLabel/FloatLabel";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import BriskManageWallet from "./components/BriskManageWallet";
import {
    GetUser,
    getBriskWalletReport,
    getBriskWalletAmount,
    addBriskWalletAmount,
} from "../../Service/_index";
import WalletTopupDialog from "./components/WalletTopupDialog";
import StatusResultDialog from "./components/StatusResultDialog";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { checkUserType } from "../../Shared/CommonConstant";
import Loader from "../../UiComponents/Loader/Loader";
import { useEffect } from "react";
import MasterCommonTable from "../../UiComponents/CommonTable/MasterCommonTable";
import { Divider, Stack, Badge } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import wallett from "../../Resources/Images/wallet.png";
import { ToastError } from "../../UiComponents/Toaster/Toast";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import { SearchIcon } from "../../Resources/Icons/icons";

const BriskWallet = () => {
    const UserType = sessionStorage.getItem("userType");
    const navigate = useNavigate();

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [selectedData, setSelectedData] = React.useState({});
    const [openDrawer, setOpenDrawer] = React.useState(false);
    const [wallet, setWallet] = React.useState([]);
    const [openLoader, setOpenLoader] = useState(false);
    const [isAllow, setIsAllow] = useState({});

    const [selectedUser, setSelecteduser] = useState("");
    const [userDetails, setUserDetails] = useState([]);
    const [totalWallet, setTotalWallet] = useState(0);
    const [showFilter, setShowFilter] = useState(false);
    const [originalData, setOriginalData] = useState([]);
    const [searchText, setSearchText] = useState("");

    // Topup State
    const [walletTopupDialog, setWalletTopupDialog] = useState(false);
    const [topupAmount, setTopupAmount] = useState("");
    const [topupFile, setTopupFile] = useState(null);
    const [amountError, setAmountError] = useState("");
    const [fileError, setFileError] = useState("");
    const [userError, setUserError] = useState("");
    const [selectedTopupUser, setSelectedTopupUser] = useState(null);
    const [resultDialog, setResultDialog] = useState({ open: false, type: "", message: "" });

    const activeFiltersCount = selectedUser ? 1 : 0;

    const GetUserDetails = () => {
        const bodyData = {
            requesttype: "TABLE",
            isAscending: true,
        };
        const sessionUserId = sessionStorage.getItem("userId");
        const isUserRole = UserType && UserType.toUpperCase() === "USER";

        GetUser(bodyData)
            .then((res) => {
                const FilteredData = res?.data
                    .map((e) => {
                        // If user is strictly a USER, only show themselves
                        if (isUserRole && e._id !== sessionUserId) {
                            return null;
                        }

                        if (e.userType.includes("user")) {
                            return {
                                ...e,
                                label: e.name + " - " + e.mobileNumber + " - " + e.email,
                                value: e?._id,
                            };
                        } else {
                            return null;
                        }
                    })
                    .filter(Boolean);
                setUserDetails(FilteredData);
            })
            .catch((err) => ToastError("Network Error"));
    };

    useEffect(() => {
        GetUserDetails();
    }, []);

    const calculateSum = (arr) => {
        return arr?.reduce((total, current) => {
            return Number(total) + Number(current);
        }, 0);
    };

    const onSearch = (e) => {
        const searchTerm = e.target.value.toLowerCase();
        if (searchTerm === "") {
            setWallet(originalData);
        } else {
            const filteredData = originalData.filter((item) => {
                return Object.values(item).some(
                    (value) =>
                        value && value.toString().toLowerCase().includes(searchTerm)
                );
            });
            setWallet(filteredData);
        }
        setPage(0);
    };

    const GetData = async () => {
        setOpenLoader(true);
        getBriskWalletReport({ userRef: selectedUser?._id || selectedUser })
            .then((res) => {
                setOpenLoader(false);
                if (res && !res.error && Array.isArray(res)) {
                    const dataWithAction = res.map((row, index) => ({
                        ...row,
                        __index__: index + 1, // Add index for S.No column
                        action: (
                            <Button onClick={() => handleStatementClick(row)}>Statement</Button>
                        ),
                    }));

                    setWallet(dataWithAction);
                    setOriginalData(dataWithAction);

                    const totalWalletValues = res.map((e) => e?.balance || 0);
                    setTotalWallet(calculateSum(totalWalletValues));
                } else {
                    setWallet([]);
                    setOriginalData([]);
                    setTotalWallet(0);
                }
                setPage(0);
            })
            .catch((err) => {
                setOpenLoader(false);
                setWallet([]);
                setOriginalData([]);
                setTotalWallet(0);
            });
    };

    const GetWalletBalance = async () => {
        getBriskWalletAmount()
            .then((res) => {
                if (res && !res.error) {
                    // Balance is already included in the wallet report
                    console.log("Wallet balance:", res.balance);
                }
            })
            .catch((err) => {
                console.error("Failed to fetch wallet balance");
            });
    };

    const GetUnfilteredData = async () => {
        setOpenLoader(true);
        getBriskWalletReport({ userRef: null })
            .then((res) => {
                setOpenLoader(false);
                if (res && !res.error && Array.isArray(res)) {
                    const dataWithAction = res.map((row, index) => ({
                        ...row,
                        __index__: index + 1, // Add index for S.No column
                        action: (
                            <Button onClick={() => handleStatementClick(row)}>Statement</Button>
                        ),
                    }));

                    setWallet(dataWithAction);
                    setOriginalData(dataWithAction);

                    const totalWalletValues = res.map((e) => e?.balance || 0);
                    setTotalWallet(calculateSum(totalWalletValues));
                } else {
                    setWallet([]);
                    setOriginalData([]);
                    setTotalWallet(0);
                }
                setPage(0);
            })
            .catch((err) => {
                setOpenLoader(false);
            });
    };

    const handleClearAllState = () => {
        setSelecteduser(null);
        GetUnfilteredData();
    };

    const handleSaveAllFilter = () => {
        GetData();
        setShowFilter(false);
    };

    const handleTransactionHistory = (row) => {
        navigate("/briskTransactionHistory", {
            state: {
                userId: row.userId,
                userName: row.userName,
            },
        });
    };

    const handleStatementClick = (row) => {
        setSelectedData(row);
        setOpenDrawer(true);
    };

    const handleWalletTopup = () => {
        setTopupAmount("");
        setTopupFile(null);
        setAmountError("");
        setFileError("");
        setUserError("");
        setSelectedTopupUser(null);
        setWalletTopupDialog(true);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setFileError("");

        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setFileError("File size must be less than 5MB");
                event.target.value = null;
                setTopupFile(null);
                return;
            }
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
            if (!allowedTypes.includes(file.type)) {
                setFileError("Only images (JPEG, PNG, GIF) and PDF files are allowed");
                event.target.value = null;
                setTopupFile(null);
                return;
            }
            setTopupFile(file);
        }
    };

    const confirmWalletTopup = () => {
        setAmountError("");
        setFileError("");
        setUserError("");

        let hasError = false;

        const numAmount = Number(topupAmount);
        if (!topupAmount || isNaN(numAmount) || numAmount <= 0) {
            setAmountError("Please enter a valid amount");
            hasError = true;
        }

        // if (!topupFile) {
        //     setFileError("Please upload a receipt/proof of payment");
        //     hasError = true;
        // }

        // Determine target user ID
        let targetUserId = null;
        if (!isAllow.isUser) {
            // For admin/non-user: if a user is selected, use that; otherwise use logged-in user
            if (selectedTopupUser) {
                targetUserId = selectedTopupUser._id || selectedTopupUser.value;
            } else {
                // No user selected, use the logged-in user's ID
                targetUserId = sessionStorage.getItem("userId");
            }
        } else {
            // For regular users, always use their own ID
            targetUserId = sessionStorage.getItem("userId");
        }

        if (hasError) return;

        setWalletTopupDialog(false);
        setOpenLoader(true);

        const formData = new FormData();
        formData.append('amount', numAmount);
        if (targetUserId) {
            formData.append('userId', targetUserId);
        }
        formData.append('file', topupFile);

        addBriskWalletAmount(formData)
            .then((res) => {
                setOpenLoader(false);
                if (res && !res.error && res.success) {
                    setResultDialog({
                        open: true,
                        type: "success",
                        message: res.message || "Wallet topup successful"
                    });
                    if (selectedUser) {
                        GetData();
                    } else {
                        GetUnfilteredData();
                        GetWalletBalance();
                    }

                } else {
                    setResultDialog({
                        open: true,
                        type: "error",
                        message: res.message || "Failed to initiate topup"
                    });
                }
            })
            .catch((err) => {
                setOpenLoader(false);
                setResultDialog({
                    open: true,
                    type: "error",
                    message: "Failed to initiate topup"
                });
            });
    };

    const headCells = [
        {
            id: "sno",
            placeMent: true,
            disablePadding: false,
            label: "S.No",
            type: "index"
        },
        {
            id: "userName",
            placeMent: true,
            disablePadding: false,
            label: "User",
        },
        {
            id: "createdByName",
            placeMent: true,
            disablePadding: false,
            label: "Created By",
        },
        {
            id: "balance",
            placeMent: true,
            disablePadding: false,
            label: "Balance",
            type: "component",
            renderComponent: (row) => (
                <Typography
                    sx={{
                        color: row.balance >= 0 ? "green" : "red",
                        fontWeight: "500",
                    }}
                >
                    ₹ {Number(row.balance || 0).toLocaleString("en-IN")}
                </Typography>
            ),
        },
        {
            id: "pendingBalance",
            placeMent: true,
            disablePadding: false,
            label: "Pending Balance",
            type: "component",
            renderComponent: (row) => (
                <Typography
                    sx={{
                        color: "orange",
                        fontWeight: "500",
                    }}
                >
                    ₹ {Number(row.pendingBalance || 0).toLocaleString("en-IN")}
                </Typography>
            ),
        },
        {
            id: "action",
            placeMent: false,
            disablePadding: false,
            label: "Action",
            type: "component",
            renderComponent: (row) => (
                <ButtonGroup size="small" variant="contained" sx={{ gap: 1 }}>
                    <Button
                        onClick={() => handleStatementClick(row)}
                        sx={{
                            backgroundColor: "#1E3A5F",
                            "&:hover": { backgroundColor: "#2E5A8F" }
                        }}
                    >
                        Statement
                    </Button>
                    {!isAllow.isUser && (
                        <Button
                            onClick={() => handleTransactionHistory(row)}
                            sx={{
                                backgroundColor: "#D4AF37",
                                color: "#1E3A5F",
                                "&:hover": { backgroundColor: "#C9A961" }
                            }}
                        >
                            Summary
                        </Button>
                    )}
                </ButtonGroup>
            ),
        },
    ];

    const tableConfig = {
        isAction: false,
        isView: false,
        isEdit: false,
        isDelete: false,
        isSearch: false,
        isUpload: false,
        isFileDownload: false,
    };

    React.useEffect(() => {
        const renderData = checkUserType(UserType);
        setIsAllow(renderData);
        GetData();
        GetWalletBalance();
    }, [UserType]);

    const formatNumber = (num) => new Intl.NumberFormat("en-IN").format(num);

    return (
        <>
            <div>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ position: "relative", zIndex: 1, mt: 0 }}>
                        <Grid container className="Master_Card_Container">
                            <Grid
                                container
                                alignItems="center"
                                className="Master_Card_Sub_Container"
                                spacing={2}
                            >
                                <Grid item xs={12} sm={6} md={6}>
                                    <Typography className="Master_Card_Tittle">
                                        Brisk Wallet
                                    </Typography>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={6}
                                    className="d-flex justify-content-end"
                                    gap={"8px"}
                                    sx={{ display: "flex" }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{ alignItems: "center" }}
                                    >
                                        {!isAllow.isUser && (
                                            <Button
                                                className="Master_Card_Filter_Button"
                                                sx={{ width: "102px", height: "40px", gap: "8px" }}
                                                startIcon={
                                                    <Badge
                                                        badgeContent={activeFiltersCount}
                                                        sx={{
                                                            "& .MuiBadge-badge": {
                                                                right: 5,
                                                                top: 3,
                                                                width: "14px",
                                                                height: "14px",
                                                                minWidth: "14px",
                                                                borderRadius: "50%",
                                                                fontSize: "8.91px",
                                                                fontWeight: "600",
                                                                backgroundColor: "#E50000",
                                                                color: "#fff",
                                                                padding: 0,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            },
                                                        }}
                                                    >
                                                        <FilterListIcon sx={{ marginRight: "1.5px" }} />
                                                    </Badge>
                                                }
                                                onClick={() => setShowFilter((s) => !s)}
                                                size="small"
                                                variant="outlined"
                                            >
                                                Filters
                                            </Button>
                                        )}
                                        <Button
                                            variant="contained"
                                            startIcon={<AccountBalanceWalletIcon />}
                                            onClick={handleWalletTopup}
                                            sx={{
                                                textTransform: "none",
                                                background: "linear-gradient(135deg, #1E3A5F 0%, #2E5A8F 100%)",
                                                color: "#fff",
                                                fontFamily: "Inter",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                padding: "5px 15px",
                                                borderRadius: "8px",
                                                height: "40px",
                                                boxShadow: "0 4px 12px rgba(30, 58, 95, 0.3)",
                                                transition: "all 0.3s ease",
                                                "&:hover": {
                                                    background: "linear-gradient(135deg, #2E5A8F 0%, #1E3A5F 100%)",
                                                    transform: "translateY(-2px)",
                                                    boxShadow: "0 6px 16px rgba(30, 58, 95, 0.4)",
                                                },
                                            }}
                                        >
                                            Wallet Topup
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>

                            {showFilter && !isAllow.isUser && (
                                <Grid item xs={12} sx={{ mb: 2 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={4} md={3}>
                                            <FloatLabel label="Select User" value={selectedUser?.label || ""}>
                                                <Autocomplete
                                                    className="AutoComplete_InputBox"
                                                    disablePortal
                                                    options={userDetails}
                                                    value={selectedUser}
                                                    onChange={(e, v) => setSelecteduser(v || null)}
                                                    renderInput={(params) => <TextField {...params} />}
                                                    renderOption={(props, option) => (
                                                        <li {...props} key={option._id}>
                                                            {option.name + " - " + option.mobileNumber + " - " + option.email}
                                                        </li>
                                                    )}
                                                    isOptionEqualToValue={(option, value) => option._id === value._id}
                                                />
                                            </FloatLabel>
                                        </Grid>
                                        <Grid item xs={12} sm={8} md={9} sx={{ display: "flex", gap: 2 }}>
                                            <Button
                                                variant="contained"
                                                onClick={handleSaveAllFilter}
                                                sx={{ textTransform: "none" }}
                                            >
                                                Search
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={handleClearAllState}
                                                sx={{ textTransform: "none" }}
                                            >
                                                Clear All
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Divider
                                    sx={{
                                        backgroundColor: "#EAECF0",
                                        borderWidth: 1,
                                    }}
                                />
                            </Grid>
                            <Grid
                                container
                                alignItems={"center"}
                                justifyContent={"space-between"}
                            >
                                <Grid
                                    item
                                    marginLeft={2}
                                    margin={1}
                                    sx={{
                                        backgroundColor: "#F0F5FA",
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "2px",
                                        paddingRight: "15px",
                                        paddingLeft: "15px",
                                        gap: "10px",
                                        border: "1px solid #DFE7F1",
                                    }}
                                >
                                    <Grid>
                                        <img src={wallett} width="18px" height="17px" alt="wallet" />
                                    </Grid>
                                    <Grid>
                                        <Typography
                                            sx={{
                                                fontFamily: "Inter",
                                                fontWeight: "500",
                                                color: "#0070FF",
                                                fontSize: 16,
                                                lineHeight: "1.2",
                                            }}
                                        >
                                            ₹ {formatNumber(totalWallet)}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: "Inter",
                                                fontWeight: "500",
                                                color: "#667085",
                                                fontSize: 12,
                                                lineHeight: "1.2",
                                            }}
                                        >
                                            Total Wallet
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} sm={4} md={3.1} margin={1}>
                                    <Input
                                        focused="false"
                                        className="w-100 Master_Card_Input"
                                        onChange={onSearch}
                                        disableUnderline={true}
                                        placeholder="Search User"
                                        startAdornment={
                                            <InputAdornment
                                                position="start"
                                                sx={{
                                                    marginRight: "8px",
                                                }}
                                            >
                                                <SearchIcon />
                                            </InputAdornment>
                                        }
                                    />
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <MasterCommonTable
                                    data={wallet}
                                    columns={headCells}
                                    tableConfig={tableConfig}
                                    page={page}
                                    setPage={setPage}
                                    rowsPerPage={rowsPerPage}
                                    setRowsPerPage={setRowsPerPage}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </div>

            {openDrawer && (
                <BriskManageWallet
                    open={openDrawer}
                    selectedWallet={selectedData}
                    onClose={() => setOpenDrawer(false)}
                />
            )}

            <Loader open={openLoader} />

            <WalletTopupDialog
                open={walletTopupDialog}
                onClose={() => setWalletTopupDialog(false)}
                topupAmount={topupAmount}
                setTopupAmount={setTopupAmount}
                topupFile={topupFile}
                handleFileChange={handleFileChange}
                onConfirm={confirmWalletTopup}
                amountError={amountError}
                fileError={fileError}
                // Admin specific props
                showUserSelect={!isAllow.isUser}
                userList={userDetails}
                selectedUser={selectedTopupUser}
                handleUserChange={(e, v) => setSelectedTopupUser(v)}
                userError={userError}
            />

            <StatusResultDialog
                open={resultDialog.open}
                onClose={() => setResultDialog({ open: false, type: "", message: "" })}
                type={resultDialog.type}
                message={resultDialog.message}
            />
        </>
    );
};

export default BriskWallet;
